<template>
    <div
        :class="{
            'assistent-detail': true,
            'assistent-detail--heyloha': hasHeylohaBranding(),
        }"
    >
        <div class="assistent-detail__menu">
            <flex-container>
                <bar-skeleton
                    dark
                    :width="0"
                    :height="70"
                />
            </flex-container>

            <flex-container
                :padding-y="Size.lg"
                :padding-x="Size.sm"
            >
                <assistent-detail-menu-item
                    v-for="inboxFolder in InboxFolder.values"
                    :key="inboxFolder.value"
                    :inbox-folder="inboxFolder"
                    :count="0"
                    :alert-count="0"
                    :active="false"
                />
            </flex-container>

            <horizontal-line no-margin />

            <flex-container
                :padding-y="Size.md"
                :padding-x="Size.sm"
                :gap="Size.sm"
            >
                <flex-container
                    no-wrap
                    :gap="Size.xs"
                    align-items="center"
                >
                    <svg-icon
                        :icon="LabelIcon"
                        :size="IconSize.sm"
                        color="gray-400"
                    />
                    <text-decorator bold :size="FontSize.xs">
                        <span>Labels</span>
                    </text-decorator>
                </flex-container>

                <bar-skeleton
                    v-for="i in 5"
                    :key="i"
                    :width="140"
                    :height="18"
                />
            </flex-container>
        </div>
        <div class="assistent-detail__chats">
            <flex-container
                color="white"
                :padding-x="Size.md"
                justify-content="space-between"
                align-items="center"
                align-content="center"
            >
                <text-decorator>
                    <bar-skeleton :width="85" :height="16" />
                </text-decorator>
                <input-group model-value="Placeholder select-input" inline>
                    <template #input>
                        <select-input
                            :items="[]"
                            variant="inline"
                            no-scroll
                            disabled
                        >
                            <template #active>
                                <flex-container
                                    :gap="Size.xs"
                                    inline
                                    size="auto"
                                >
                                    <svg-icon
                                        :icon="BarsFilterIcon"
                                        is-custom-icon
                                        :size="IconSize.xs"
                                    />
                                    <span><bar-skeleton :width="60" :height="18" /></span>
                                </flex-container>
                            </template>
                            <template #item-active>
                                <div />
                            </template>
                        </select-input>
                    </template>
                </input-group>
            </flex-container>

            <div style="height: calc(100% - 80px)">
                <flex-container :padding="Size.sm" :gap="Size.sm">
                    <chat-card-skeleton
                        v-for="i in 5"
                        :key="i"
                    />
                </flex-container>
            </div>
        </div>
        <div class="assistent-detail__chat">
            <flex-container
                color="white"
                :padding-x="Size.md"
                justify-content="space-between"
                align-items="center"
                align-content="center"
            >
                <flex-container
                    align-items="center"
                    :size="5"
                    :gap="Size.xs"
                >
                    <bar-skeleton :width="120" />
                </flex-container>
                <flex-container
                    :size="5"
                    align-items="center"
                    justify-content="flex-end"
                >
                    <button-plain-icon
                        :icon="IdCardIcon"
                        is-custom-icon
                        :size="IconSize.sm"
                        disabled
                    />
                    <button-plain-icon
                        :icon="ArrowTurnRightIcon"
                        is-custom-icon
                        :size="IconSize.sm"
                        disabled
                    />
                    <button-plain-icon
                        :icon="StarIcon"
                        is-custom-icon
                        :size="IconSize.sm"
                        disabled
                    />
                </flex-container>
            </flex-container>
            <div class="assistent-detail__chat__list">
                <chat-interaction-skeleton />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { onMounted } from 'vue';
    import {
        FlexContainer,
        FontSize,
        IconSize,
        InputGroup,
        SelectInput,
        Size,
        SvgIcon,
        TextDecorator,
        HorizontalLine,
        ButtonPlainIcon,
    } from '@keyvue/one-ui';
    import BarsFilterIcon from '@keyvue/one-awesome-icons/icons/regular/bars-filter.svg?component';
    import ChatCardSkeleton from '@/ai/assistent/components/skeleton/ChatCardSkeleton.vue';
    import InboxFolder from '@/ai/assistent/types/InboxFolder';
    import AssistentDetailMenuItem from '@/ai/assistent/components/button/AssistentDetailMenuItemButton.vue';
    import LabelIcon from '@keyvue/one-material-icons/icons/label.svg?component';
    import IdCardIcon from '@keyvue/one-awesome-icons/icons/regular/id-card.svg?component';
    import ArrowTurnRightIcon from '@keyvue/one-awesome-icons/icons/regular/arrow-turn-right.svg?component';
    import StarIcon from '@keyvue/one-awesome-icons/icons/regular/star.svg?component';
    import ChatInteractionSkeleton from '@/ai/assistent/components/ChatInteractionSkeleton.vue';
    import { Assistent } from '@keyvue/one-api-client';
    import { useRouter } from 'vue-router';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    const router = useRouter();
    const { client } = useClient();
    const { hasHeylohaBranding } = useAppBranding();

    onMounted(async () => {
        const params = {
            pagination: false,
        };

        const assistents = await client.getCollection<Assistent>(Assistent, params);
        if (assistents.data.length === 0) {
            await router.push({ name: 'assistent-dashboard' });
            return;
        }

        let assistentId = assistents.data[0].id.value;
        if (typeof localStorage !== 'undefined') {
            const lastVisitedAssistentId = localStorage.getItem('last_visisted_assistent_id');
            if (lastVisitedAssistentId !== null) {
                assistentId = lastVisitedAssistentId;
            }
        }

        await router.push({ name: 'assistent-detail', params: { assistentId } });
    });
</script>

<style scoped lang="scss">
.assistent-detail {
    display: grid;
    grid-template-columns: 20% 25% 55%;
    grid-template-rows: 100%;
    height: 100%;
    width: 100%;

    &__menu {
        height: 100%;
        background-color: white;
        padding: size(sm);
        box-sizing: border-box;
        border-right: 1px solid var(--theme-colors-default--gray-300);
        overflow-y: scroll;
    }

    &__chats {
        height: 100%;
        background-color: var(--theme-colors-default--gray-100);
        box-sizing: border-box;
        border-right: 1px solid var(--theme-colors-default--gray-300);

        & > div:first-child {
            height: 80px;
            border-bottom: 1px solid var(--theme-colors-default--gray-300)
        }
    }

    &__chat {
        height: 100%;
        background-color: var(--theme-colors-default--gray-50);
        box-sizing: border-box;
        border-right: 1px solid var(--theme-colors-default--gray-300);

        & > div:first-child {
            height: 80px;
            border-bottom: 1px solid var(--theme-colors-default--gray-300)
        }

        &__email {
            height: calc(100% - 80px);
            overflow-y: scroll;
        }
    }

    &__dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background-color: color(gray-400);
    }
}

.assistent-detail--heyloha {
    .assistent-detail__chats,
    .assistent-detail__chat {
        background-color: #FFFCF3;
    }

    .assistent-detail__chat__list {
        background-image: url('@/ai/assistent/assets/inbox-background-heyloha.png');
        background-repeat: no-repeat;
        background-position: center center;
        background-size: cover;
    }
}
</style>
